/**
 * プラン上限の告知とアップグレード導線。
 *
 * 上限は「押した瞬間にエラー」ではなく **一覧の上部に常時**出す（05-ui.md §8.4）。
 * 押してから断られるのは体験として最悪なので、作成ボタン自体を無効にして
 * 理由を添える。サーバーからの 409 `PLAN_LIMIT_EXCEEDED` も同じ見た目に寄せる。
 */
import * as React from 'react';
import { Alert, AlertDescription, AlertTitle, Button,
  formatCount,
} from '@mail-sv/ui';
import { planLimitDetailsSchema } from '@mail-sv/shared';
import { ArrowUpRight } from 'lucide-react';
import { ApiError } from '../lib/api.js';
import { AppLink } from '../lib/nav.js';

/** 上限に達しているか。0 は無制限。 */
export function isAtLimit(current: number, max: number): boolean {
  if (max === 0) return false;
  return current >= max;
}

export function PlanLimitAlert({
  resourceLabel,
  current,
  max,
  /** 課金権限が無いユーザーには「管理者に依頼」を促す。 */
  canUpgrade = true,
}: {
  resourceLabel: string;
  current: number;
  max: number;
  canUpgrade?: boolean;
}) {
  if (!isAtLimit(current, max)) return null;
  return (
    <Alert variant="warning">
      <AlertTitle>
        {resourceLabel}の上限（{formatCount(max)} 件）に達しています
      </AlertTitle>
      <AlertDescription className="space-y-3">
        <p>
          現在 {formatCount(current)} 件です。これ以上追加するには、
          {canUpgrade
            ? 'プランを変更するか、使っていない項目を削除してください。'
            : '組織の管理者にプラン変更を依頼するか、使っていない項目を削除してください。'}
        </p>
        {canUpgrade ? (
          <Button asChild variant="outline" size="sm">
            <AppLink to="/billing/plan">
              プランを変更する
              <ArrowUpRight aria-hidden />
            </AppLink>
          </Button>
        ) : null}
      </AlertDescription>
    </Alert>
  );
}

/**
 * サーバーが返した `PLAN_LIMIT_EXCEEDED` を同じ見た目で出す。
 * details の形は `@mail-sv/shared` の `planLimitDetailsSchema` が唯一の正。
 */
export function PlanLimitFromError({ error }: { error: unknown }) {
  const details = React.useMemo(() => {
    if (!(error instanceof ApiError) || !error.is('PLAN_LIMIT_EXCEEDED')) return null;
    const parsed = planLimitDetailsSchema.safeParse(error.details);
    return parsed.success ? parsed.data : null;
  }, [error]);

  if (!details) return null;

  return (
    <PlanLimitAlert
      resourceLabel={RESOURCE_LABEL[details.resource] ?? details.resource}
      current={details.current}
      max={details.max}
    />
  );
}

const RESOURCE_LABEL: Record<string, string> = {
  mailbox: 'メールアドレス',
  domain: 'ドメイン',
  alias: 'エイリアス',
  storage: '容量',
  forwarder: '転送設定',
  member: 'メンバー',
  /*
   * **`private_address` は上限 0 のとき何も表示されない。**
   * `isAtLimit()` が 0 を無制限として扱うためで、これは仕様どおり
   * （0 = 機能なし の案内は `routes/private-addresses.tsx` が専用の文面で出す）。
   * ここに載せてあるのは上限 > 0 で埋まったときのためだけ。
   */
  private_address: 'プライベートメールアドレス',
};
